import React, { useEffect, useState } from 'react';
import { Box, thc, ToastConsumer } from '@flightstats/airframe-react';
import store from '../../redux/store';

import { selectTravelerProfile } from '../../redux/entities/travelers/travelerProfileSelectors';
import { fetchAgentTravelerProfile } from '../../redux/entities/travelers/travelerProfileActions';
import { TravelerProfileFormHandler } from '../../traveler/profile/TravelerProfileFormHandler';
import { travelerForgotPassword } from '../../service/travelerLoginService';
import type { AddToast } from '../../types/airframe';

import PageTitle from '../../pages/components/pageTitle';
import { TravelerProfileForm } from '../../traveler/profile/TravelerProfileForm';
import CiriumWrapperWithoutGlobalStyles from '../../components/cirium/CiriumWrapperWithoutGlobalStyles';
import FullPageLoadingSpinner from '../../pages/components/fullPageLoadingSpinner';
import { hasWorldAware } from '../../configTdm';
import { useParams } from 'react-router-dom';
import type { TravelerProfile } from '../../types/travelerProfile';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { getProperty } from '../../fsConfig';

const FormWrapper = thc(Box, {
  backgroundColor: '#7A8794',
  padding: '10px 20px 20px',
});

const TravelerProfileContainer: React.FC = () => { 
  // TODO: this should propbably just be pulled from props.initialTravelerProfile to match the traveler version of this page.
  const dispatch = useDispatch();
  const { travelerKey } = useParams<{ travelerKey: string }>();
  const initialTravelerProfile = useSelector(selectTravelerProfile);
  const user = getProperty('session', {});

  const [email, setEmail] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordReset, setPasswordReset] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    dispatch(fetchAgentTravelerProfile({ includeHealthAlerts: hasWorldAware(), travelerKey }))
      .then((result: any) => {
        setLoading(false);
        setError(!!result.error);
        // TODO: this network call is largely unnecessary, as email is in props.initialTravelerProfile
        result.response && setEmail(result.response.email);
      });
  }, []);

  const handleResetPasswordSubmit = (email: string, addToast: AddToast) => {
    setLoading(true);
    travelerForgotPassword({ email })
      .then(() => setPasswordReset(true))
      .catch(_error => {
        addToast('Reset password is temporarily unavailable.', {
          variant: 'error',
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <CiriumWrapperWithoutGlobalStyles>
      <PageTitle>TRAVELER PROFILE</PageTitle>
      <Box>
        {loading && <FullPageLoadingSpinner />}
        {error ? (
          <div className="alert alert-danger" role="alert">
            Failed to load traveler profile. Please try again.
          </div>
        ) : (
          <ToastConsumer>
            {({ addToast }) => (
              <FormWrapper>
                {email && initialTravelerProfile && travelerKey && (
                  <TravelerProfileFormHandler
                    accountId={user.accountId}
                    isAgent={true}
                    loading={loading}
                    setLoading={setLoading}
                    onSaveSuccess={values => setEmail(values.email)}
                    travelerKey={travelerKey}
                    travelerProfile={initialTravelerProfile}
                    addToast={addToast}
                    render={(
                      handleTestAlert,
                      testAlertSent,
                      testAlertSentContact,
                      handleOnCloseTestAlertSent,
                      handleSubmit,
                      loading,
                      hasFormError,
                      testAlertError,
                      saveProfilePhoneError,
                      setSaveProfilePhoneError,
                    ) => (
                      <TravelerProfileForm
                        saveProfilePhoneError={saveProfilePhoneError}
                        setSaveProfilePhoneError={setSaveProfilePhoneError}
                        travelerKey={travelerKey}
                        email={email}
                        disabled={loading}
                        onSubmit={handleSubmit}
                        onTestAlert={handleTestAlert}
                        testAlertSent={testAlertSent}
                        onCloseTestAlertSent={handleOnCloseTestAlertSent}
                        testAlertSentContact={testAlertSentContact}
                        onResetPasswordSubmit={handleResetPasswordSubmit}
                        passwordReset={passwordReset}
                        passwordResetConfirmation={() =>
                          setPasswordReset(false)
                        }
                        hasFormError={hasFormError}
                        supportedSMSCountryCodes={
                          user.supportedSMSCountryCodes
                        }
                        testAlertError={testAlertError}
                      />
                    )}
                  />
                )}
              </FormWrapper>
            )}
          </ToastConsumer>
        )}
      </Box>
    </CiriumWrapperWithoutGlobalStyles>
  );
};

const WrappedTravelerProfileContainer: React.FC = () => (
  <Provider store={store}>
    <TravelerProfileContainer />
  </Provider>
);

export default WrappedTravelerProfileContainer;
